<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import {
  pullCatalogItemsFromPriority,
  pullCatalogImagesFromPriority,
} from '../../../core/services/catalog.js'
import { pullClientsFromPriority } from '../../../core/services/clients.js'
import {
  pullPriceListsFromPriority,
  pullAmountDiscountsFromPriority,
} from '../../../core/services/prices.js'
import LoadingSpinner from '../../components/LoadingSpinner.vue'
import { useCatalogStore } from '@app/store/catalog.js'
import { useAuthStore } from '@app/store/auth.js'
import { useToast } from '@core/composables/useToast.js'

const catalogStore = useCatalogStore()
const authStore = useAuthStore()
const { success: toastSuccess } = useToast()

async function refreshCatalogStores() {
  try {
    catalogStore.reset()
    await catalogStore.bootstrap(authStore.user?.profile)
    toastSuccess('הנתונים המקומיים עודכנו בהצלחה')
  } catch (err) {
    console.error('Failed to refresh catalog stores:', err)
  }
}

// ============================================
// Catalog Sync State
// ============================================
const isLoading = ref(false)
const errorMessage = ref('')
const result = ref(null)
const lastRunAt = ref(null)

const showNewItemsModal = ref(false)
const showRemovedItemsModal = ref(false)

// Computed
const hasResult = computed(() => result.value !== null)
const hasError = computed(() => errorMessage.value !== '')

// Escape key handler for modal
function handleEscape(e) {
  if (e.key === 'Escape') {
    showNewItemsModal.value = false
    showRemovedItemsModal.value = false
  }
}

onMounted(() => document.addEventListener('keydown', handleEscape))
onUnmounted(() => document.removeEventListener('keydown', handleEscape))

// ============================================
// Images Loading State
// ============================================
const imagesIsRunning = ref(false)
const imagesStopRequested = ref(false)
const imagesCursor = ref(null)
const imagesDone = ref(false)
const imagesErrorMessage = ref('')
const imagesBatchSize = ref(50)
const imagesTestMode = ref(false)

// Cumulative totals
const imagesTotalProcessed = ref(0)
const imagesTotalSuccess = ref(0)
const imagesTotalFailed = ref(0)
const imagesTotalSkipped = ref(0)
const imagesBatchesCompleted = ref(0)

// Last batch result for display
const imagesLastBatchResult = ref(null)
const imagesLastBatchErrors = ref([])

// Computed
const imagesHasError = computed(() => imagesErrorMessage.value !== '')
const imagesCanResume = computed(
  () => !imagesIsRunning.value && imagesCursor.value !== null && !imagesDone.value
)
const imagesShowStats = computed(() => imagesBatchesCompleted.value > 0 || imagesIsRunning.value)

// ============================================
// Clients Sync State
// ============================================
const clientsLoading = ref(false)
const clientsErrorMessage = ref('')
const clientsResult = ref(null)
const clientsLastRunAt = ref(null)

// Computed
const clientsHasResult = computed(() => clientsResult.value !== null)
const clientsHasError = computed(() => clientsErrorMessage.value !== '')

// ============================================
// Price Lists Sync State
// ============================================
const pricesLoading = ref(false)
const pricesErrorMessage = ref('')
const pricesResult = ref(null)
const pricesLastRunAt = ref(null)

// Computed
const pricesHasResult = computed(() => pricesResult.value !== null)
const pricesHasError = computed(() => pricesErrorMessage.value !== '')

// ============================================
// Amount Discounts Sync State
// ============================================
const discountsLoading = ref(false)
const discountsErrorMessage = ref('')
const discountsResult = ref(null)
const discountsLastRunAt = ref(null)

// Computed
const discountsHasResult = computed(() => discountsResult.value !== null)
const discountsHasError = computed(() => discountsErrorMessage.value !== '')

// ============================================
// Format Helpers
// ============================================
function formatTimestamp(date) {
  if (!date) return ''
  return new Intl.DateTimeFormat('he-IL', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(date)
}

// ============================================
// Catalog Sync Handler
// ============================================
async function handlePullItems() {
  errorMessage.value = ''
  result.value = null
  isLoading.value = true

  try {
    const response = await pullCatalogItemsFromPriority()
    result.value = response
    lastRunAt.value = new Date()
    refreshCatalogStores()
  } catch (error) {
    console.error('Pull failed:', error)
    if (error.code && error.message) {
      errorMessage.value = `${error.code}: ${error.message}`
    } else if (error.message) {
      errorMessage.value = error.message
    } else {
      errorMessage.value = 'שגיאה לא צפויה בעת משיכת הנתונים'
    }
  } finally {
    isLoading.value = false
  }
}

// ============================================
// Images Loading Handlers
// ============================================

/**
 * Run images loading in batches
 * @param {Object} options
 * @param {boolean} [options.resume=false] - If true, continue from stored cursor
 */
async function runImagesBatches(options = {}) {
  const { resume = false } = options

  // Reset state if not resuming
  if (!resume) {
    imagesCursor.value = null
    imagesDone.value = false
    imagesTotalProcessed.value = 0
    imagesTotalSuccess.value = 0
    imagesTotalFailed.value = 0
    imagesTotalSkipped.value = 0
    imagesBatchesCompleted.value = 0
    imagesLastBatchResult.value = null
    imagesLastBatchErrors.value = []
  }

  imagesIsRunning.value = true
  imagesStopRequested.value = false
  imagesErrorMessage.value = ''

  // In test mode: use batch size of 10 and run only one batch
  const effectiveBatchSize = imagesTestMode.value ? 10 : imagesBatchSize.value

  try {
    while (!imagesDone.value && !imagesStopRequested.value) {
      // Call the batch function
      const batchResult = await pullCatalogImagesFromPriority({
        batchSize: effectiveBatchSize,
        startAfter: imagesCursor.value,
      })

      // Update cumulative totals
      imagesTotalProcessed.value += batchResult.processed || 0
      imagesTotalSuccess.value += batchResult.success || 0
      imagesTotalFailed.value += batchResult.failed || 0
      imagesTotalSkipped.value += batchResult.skipped || 0
      imagesBatchesCompleted.value++

      // Update cursor and done state
      imagesCursor.value = batchResult.nextCursor ?? null
      imagesDone.value = !!batchResult.done

      // Store last batch result
      imagesLastBatchResult.value = batchResult

      // Store errors if any
      if (batchResult.errors && batchResult.errors.length > 0) {
        imagesLastBatchErrors.value = batchResult.errors
      }

      // In test mode, stop after first batch
      if (imagesTestMode.value) {
        imagesDone.value = true
        break
      }

      // Yield to UI to keep it responsive
      await new Promise((r) => setTimeout(r, 50))
    }
  } catch (error) {
    console.error('Images batch failed:', error)
    if (error.code && error.message) {
      imagesErrorMessage.value = `${error.code}: ${error.message}`
    } else if (error.message) {
      imagesErrorMessage.value = error.message
    } else {
      imagesErrorMessage.value = 'שגיאה לא צפויה בעת טעינת התמונות'
    }
  } finally {
    imagesIsRunning.value = false
  }
}

function handleStartImages() {
  runImagesBatches({ resume: false })
}

function handleStopImages() {
  imagesStopRequested.value = true
}

function handleResumeImages() {
  runImagesBatches({ resume: true })
}

function handleResetImages() {
  imagesCursor.value = null
  imagesDone.value = false
  imagesTotalProcessed.value = 0
  imagesTotalSuccess.value = 0
  imagesTotalFailed.value = 0
  imagesTotalSkipped.value = 0
  imagesBatchesCompleted.value = 0
  imagesLastBatchResult.value = null
  imagesLastBatchErrors.value = []
  imagesErrorMessage.value = ''
}

// ============================================
// Clients Sync Handler
// ============================================
async function handlePullClients() {
  // Reset state before starting
  clientsErrorMessage.value = ''
  clientsResult.value = null
  clientsLoading.value = true

  try {
    const response = await pullClientsFromPriority()
    clientsResult.value = response
    clientsLastRunAt.value = new Date()
    refreshCatalogStores()
  } catch (error) {
    console.error('Clients pull failed:', error)
    if (error.code && error.message) {
      clientsErrorMessage.value = `${error.code}: ${error.message}`
    } else if (error.message) {
      clientsErrorMessage.value = error.message
    } else {
      clientsErrorMessage.value = 'שגיאה לא צפויה בעת משיכת הלקוחות'
    }
  } finally {
    clientsLoading.value = false
  }
}

// ============================================
// Price Lists Sync Handler
// ============================================
async function handlePullPriceLists() {
  // Reset state before starting
  pricesErrorMessage.value = ''
  pricesResult.value = null
  pricesLoading.value = true

  try {
    const response = await pullPriceListsFromPriority()
    pricesResult.value = response
    pricesLastRunAt.value = new Date()
    refreshCatalogStores()
  } catch (error) {
    console.error('Price lists pull failed:', error)
    if (error.code && error.message) {
      pricesErrorMessage.value = `${error.code}: ${error.message}`
    } else if (error.message) {
      pricesErrorMessage.value = error.message
    } else {
      pricesErrorMessage.value = 'שגיאה לא צפויה בעת משיכת המחירונים'
    }
  } finally {
    pricesLoading.value = false
  }
}

// ============================================
// Amount Discounts Sync Handler
// ============================================
async function handlePullAmountDiscounts() {
  // Reset state before starting
  discountsErrorMessage.value = ''
  discountsResult.value = null
  discountsLoading.value = true

  try {
    const response = await pullAmountDiscountsFromPriority()
    discountsResult.value = response
    discountsLastRunAt.value = new Date()
    refreshCatalogStores()
  } catch (error) {
    console.error('Amount discounts pull failed:', error)
    if (error.code && error.message) {
      discountsErrorMessage.value = `${error.code}: ${error.message}`
    } else if (error.message) {
      discountsErrorMessage.value = error.message
    } else {
      discountsErrorMessage.value = 'שגיאה לא צפויה בעת משיכת הנחות כמות'
    }
  } finally {
    discountsLoading.value = false
  }
}

/**
 * Format duration in milliseconds to readable format
 */
function formatDuration(ms) {
  if (!ms) return '0ms'
  if (ms < 1000) return `${ms}ms`
  const seconds = Math.floor(ms / 1000)
  const remainingMs = ms % 1000
  if (seconds < 60) return `${seconds}.${Math.floor(remainingMs / 100)}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}m ${remainingSeconds}s`
}
</script>

<template>
  <div class="admin-data">
    <!-- Page Header -->
    <header class="page-header">
      <div class="header-badge">
        <svg
          class="header-icon"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
        </svg>
        <span>סנכרון</span>
      </div>
      <h1 class="page-title">ניהול נתונים</h1>
      <p class="page-subtitle">משיכת פריטים, תמונות ולקוחות מפריורטי ועדכון Firestore</p>
    </header>

    <!-- Catalog Sync Card -->
    <div class="action-card">
      <div class="action-card-header">
        <div class="card-icon catalog-icon">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
            <line x1="7" y1="7" x2="7.01" y2="7" />
          </svg>
        </div>
        <div class="card-header-text">
          <h2 class="action-card-title">סנכרון קטלוג</h2>
          <p class="action-card-desc">משיכת כל הפריטים מפריורטי ועדכון אוטומטי של אוסף Catalog</p>
        </div>
      </div>

      <div class="action-card-body">
        <button class="btn-action btn-primary" :disabled="isLoading" @click="handlePullItems">
          <LoadingSpinner v-if="isLoading" size="sm" />
          <svg
            v-else
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="23 4 23 10 17 10" />
            <polyline points="1 20 1 14 7 14" />
            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
          </svg>
          <span>{{ isLoading ? 'מושך נתונים...' : 'משוך פריטים' }}</span>
        </button>
      </div>
    </div>

    <!-- Catalog Sync Error -->
    <Transition name="fade-slide">
      <div v-if="hasError" class="error-card">
        <div class="error-card-header">
          <span class="error-icon">⚠️</span>
          <span class="error-title">שגיאה</span>
        </div>
        <p class="error-message">{{ errorMessage }}</p>
      </div>
    </Transition>

    <!-- Catalog Sync Result -->
    <Transition name="fade-slide">
      <div v-if="hasResult" class="result-card">
        <div class="result-card-header">
          <span class="result-icon">✅</span>
          <span class="result-title">הסנכרון הושלם</span>
          <span v-if="lastRunAt" class="result-timestamp">{{ formatTimestamp(lastRunAt) }}</span>
        </div>

        <div class="result-stats result-stats-6">
          <div class="stat-item">
            <span class="stat-value">{{ result.fetchedCount }}</span>
            <span class="stat-label">נשלפו מפריורטי</span>
          </div>
          <div class="stat-item stat-success">
            <span class="stat-value">{{ result.writtenCount }}</span>
            <span class="stat-label">נכתבו ל-Firestore</span>
          </div>
          <div
            class="stat-item stat-info stat-clickable"
            :class="{ 'stat-has-items': result.newCount > 0 }"
            @click="result.newCount > 0 && (showNewItemsModal = true)"
          >
            <span class="stat-value">{{ result.newCount }}</span>
            <span class="stat-label">חדשים</span>
          </div>
          <div class="stat-item stat-muted">
            <span class="stat-value">{{ result.skippedCount }}</span>
            <span class="stat-label">דולגו</span>
          </div>
          <div class="stat-item" :class="{ 'stat-warning': result.failedCount > 0 }">
            <span class="stat-value">{{ result.failedCount }}</span>
            <span class="stat-label">נכשלו</span>
          </div>
          <div
            class="stat-item stat-danger stat-clickable"
            :class="{ 'stat-has-items': result.removedCount > 0 }"
            @click="result.removedCount > 0 && (showRemovedItemsModal = true)"
          >
            <span class="stat-value">{{ result.removedCount }}</span>
            <span class="stat-label">הוסרו (Z)</span>
          </div>
        </div>

        <div class="result-footer">
          <span class="result-collection"
            >אוסף: <code>{{ result.collection }}</code></span
          >
        </div>
      </div>
    </Transition>

    <!-- ============================================ -->
    <!-- Images Loading Card (Compact) -->
    <!-- ============================================ -->
    <div class="action-card">
      <div class="action-card-header">
        <div class="card-icon images-icon">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </div>
        <div class="card-header-text">
          <h2 class="action-card-title">טעינת תמונות</h2>
          <p class="action-card-desc">משיכת תמונות מפריורטי לפריטים חסרי תמונה</p>
        </div>
      </div>

      <div class="action-card-body action-card-body-compact">
        <!-- Inline Controls Row -->
        <div class="controls-row">
          <!-- Test Mode Toggle -->
          <label class="toggle-label">
            <input
              v-model="imagesTestMode"
              type="checkbox"
              class="toggle-input"
              :disabled="imagesIsRunning"
            />
            <span class="toggle-text">בדיקה</span>
          </label>

          <!-- Batch Size (hidden in test mode) -->
          <div v-if="!imagesTestMode" class="inline-input">
            <label for="batchSize">אצווה:</label>
            <input
              id="batchSize"
              v-model.number="imagesBatchSize"
              type="number"
              min="10"
              max="200"
              :disabled="imagesIsRunning"
            />
          </div>

          <!-- Action Buttons -->
          <div class="btn-group-inline">
            <button
              v-if="!imagesIsRunning && !imagesCanResume"
              class="btn-sm btn-primary"
              @click="handleStartImages"
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              <span>טען</span>
            </button>
            <button v-if="imagesIsRunning" class="btn-sm btn-warning" @click="handleStopImages">
              <LoadingSpinner size="sm" />
              <span>עצור</span>
            </button>
            <button v-if="imagesCanResume" class="btn-sm btn-primary" @click="handleResumeImages">
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              <span>המשך</span>
            </button>
            <button
              v-if="imagesBatchesCompleted > 0 && !imagesIsRunning"
              class="btn-sm btn-secondary"
              @click="handleResetImages"
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
              <span>אפס</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Images Error -->
    <Transition name="fade-slide">
      <div v-if="imagesHasError" class="error-card">
        <div class="error-card-header">
          <span class="error-icon">⚠️</span>
          <span class="error-title">שגיאה בטעינת תמונות</span>
        </div>
        <p class="error-message">{{ imagesErrorMessage }}</p>
      </div>
    </Transition>

    <!-- Images Progress/Stats -->
    <Transition name="fade-slide">
      <div v-if="imagesShowStats" class="result-card">
        <div
          class="result-card-header"
          :class="{ 'header-running': imagesIsRunning, 'header-done': imagesDone }"
        >
          <LoadingSpinner v-if="imagesIsRunning" size="sm" />
          <span v-else-if="imagesDone" class="result-icon">✅</span>
          <span v-else class="result-icon">⏸️</span>
          <span class="result-title">
            <template v-if="imagesIsRunning">מעבד תמונות...</template>
            <template v-else-if="imagesDone">הטעינה הושלמה</template>
            <template v-else>הטעינה הופסקה</template>
          </span>
          <span class="result-timestamp">{{ imagesBatchesCompleted }} אצוות</span>
        </div>

        <div class="result-stats result-stats-4">
          <div class="stat-item">
            <span class="stat-value">{{ imagesTotalProcessed }}</span>
            <span class="stat-label">עובדו</span>
          </div>
          <div class="stat-item stat-success">
            <span class="stat-value">{{ imagesTotalSuccess }}</span>
            <span class="stat-label">הצליחו</span>
          </div>
          <div class="stat-item" :class="{ 'stat-warning': imagesTotalFailed > 0 }">
            <span class="stat-value">{{ imagesTotalFailed }}</span>
            <span class="stat-label">נכשלו</span>
          </div>
          <div class="stat-item stat-muted">
            <span class="stat-value">{{ imagesTotalSkipped }}</span>
            <span class="stat-label">דולגו</span>
          </div>
        </div>

        <!-- Last Batch Errors -->
        <Transition name="fade-slide">
          <div v-if="imagesLastBatchErrors.length > 0" class="batch-errors">
            <div class="batch-errors-header">שגיאות אחרונות:</div>
            <ul class="batch-errors-list">
              <li v-for="err in imagesLastBatchErrors" :key="err.makat">
                <code>{{ err.makat }}</code
                >: {{ err.error }}
              </li>
            </ul>
          </div>
        </Transition>
      </div>
    </Transition>

    <!-- ============================================ -->
    <!-- Clients Sync Card -->
    <!-- ============================================ -->
    <div class="action-card">
      <div class="action-card-header">
        <div class="card-icon clients-icon">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
          </svg>
        </div>
        <div class="card-header-text">
          <h2 class="action-card-title">לקוחות</h2>
          <p class="action-card-desc">
            משיכת לקוחות מפריורטי ועדכון Firestore (ClientsV2 + Assortments)
          </p>
        </div>
      </div>

      <div class="action-card-body">
        <button
          class="btn-action btn-primary"
          :disabled="clientsLoading"
          @click="handlePullClients"
        >
          <LoadingSpinner v-if="clientsLoading" size="sm" />
          <svg
            v-else
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
          </svg>
          <span>{{ clientsLoading ? 'מושך לקוחות...' : 'משוך לקוחות' }}</span>
        </button>
      </div>
    </div>

    <!-- Clients Sync Error -->
    <Transition name="fade-slide">
      <div v-if="clientsHasError" class="error-card">
        <div class="error-card-header">
          <span class="error-icon">⚠️</span>
          <span class="error-title">שגיאה במשיכת לקוחות</span>
        </div>
        <p class="error-message">{{ clientsErrorMessage }}</p>
      </div>
    </Transition>

    <!-- Clients Sync Result -->
    <Transition name="fade-slide">
      <div v-if="clientsHasResult" class="result-card">
        <div class="result-card-header">
          <span class="result-icon">✅</span>
          <span class="result-title">משיכת הלקוחות הושלמה</span>
          <span v-if="clientsLastRunAt" class="result-timestamp">{{
            formatTimestamp(clientsLastRunAt)
          }}</span>
        </div>

        <div class="result-stats result-stats-6">
          <div class="stat-item">
            <span class="stat-value">{{ clientsResult.totalFetched }}</span>
            <span class="stat-label">נמשכו מפריורטי</span>
          </div>
          <div class="stat-item stat-success">
            <span class="stat-value">{{ clientsResult.realClientsUpserted }}</span>
            <span class="stat-label">לקוחות עודכנו</span>
          </div>
          <div class="stat-item" :class="{ 'stat-warning': clientsResult.realClientsDeleted > 0 }">
            <span class="stat-value">{{ clientsResult.realClientsDeleted }}</span>
            <span class="stat-label">לקוחות נמחקו</span>
          </div>
          <div class="stat-item stat-success">
            <span class="stat-value">{{ clientsResult.assortmentsUpserted }}</span>
            <span class="stat-label">אסורטימנטים עודכנו</span>
          </div>
          <div class="stat-item" :class="{ 'stat-warning': clientsResult.failedCount > 0 }">
            <span class="stat-value">{{ clientsResult.failedCount }}</span>
            <span class="stat-label">כשלונות</span>
          </div>
          <div class="stat-item stat-muted">
            <span class="stat-value">{{ formatDuration(clientsResult.durationMs) }}</span>
            <span class="stat-label">משך זמן</span>
          </div>
        </div>

        <!-- Clients Errors List -->
        <Transition name="fade-slide">
          <div v-if="clientsResult.errors && clientsResult.errors.length > 0" class="batch-errors">
            <div class="batch-errors-header">שגיאות (עד 10 ראשונות):</div>
            <ul class="batch-errors-list">
              <li v-for="err in clientsResult.errors" :key="err.custname">
                <code>{{ err.custname }}</code
                >: {{ err.message }}
              </li>
            </ul>
          </div>
        </Transition>

        <div class="result-footer">
          <span class="result-collection"
            >אוספים: <code>ClientsV2</code>, <code>Assortments</code></span
          >
        </div>
      </div>
    </Transition>

    <!-- ============================================ -->
    <!-- Price Lists Sync Card -->
    <!-- ============================================ -->
    <div class="action-card">
      <div class="action-card-header">
        <div class="card-icon prices-icon">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
          </svg>
        </div>
        <div class="card-header-text">
          <h2 class="action-card-title">מחירונים</h2>
          <p class="action-card-desc">משיכת מחירונים מפריורטי ועדכון Firestore (Prices)</p>
        </div>
      </div>

      <div class="action-card-body">
        <button
          class="btn-action btn-primary"
          :disabled="pricesLoading"
          @click="handlePullPriceLists"
        >
          <LoadingSpinner v-if="pricesLoading" size="sm" />
          <svg
            v-else
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
          </svg>
          <span>{{ pricesLoading ? 'מושך מחירונים...' : 'משוך מחירונים' }}</span>
        </button>
      </div>
    </div>

    <!-- Price Lists Sync Error -->
    <Transition name="fade-slide">
      <div v-if="pricesHasError" class="error-card">
        <div class="error-card-header">
          <span class="error-icon">⚠️</span>
          <span class="error-title">שגיאה במשיכת מחירונים</span>
        </div>
        <p class="error-message">{{ pricesErrorMessage }}</p>
      </div>
    </Transition>

    <!-- Price Lists Sync Result -->
    <Transition name="fade-slide">
      <div v-if="pricesHasResult" class="result-card">
        <div class="result-card-header">
          <span class="result-icon">✅</span>
          <span class="result-title">משיכת המחירונים הושלמה</span>
          <span v-if="pricesLastRunAt" class="result-timestamp">{{
            formatTimestamp(pricesLastRunAt)
          }}</span>
        </div>

        <div class="result-stats result-stats-5">
          <div class="stat-item">
            <span class="stat-value">{{ pricesResult.priceListsFound }}</span>
            <span class="stat-label">מחירונים נמצאו</span>
          </div>
          <div class="stat-item stat-success">
            <span class="stat-value">{{ pricesResult.priceListsProcessed }}</span>
            <span class="stat-label">עובדו בהצלחה</span>
          </div>
          <div class="stat-item stat-success">
            <span class="stat-value">{{ pricesResult.itemsUpserted }}</span>
            <span class="stat-label">פריטים עודכנו</span>
          </div>
          <div class="stat-item" :class="{ 'stat-warning': pricesResult.failedCount > 0 }">
            <span class="stat-value">{{ pricesResult.failedCount }}</span>
            <span class="stat-label">נכשלו</span>
          </div>
          <div class="stat-item stat-muted">
            <span class="stat-value">{{ formatDuration(pricesResult.durationMs) }}</span>
            <span class="stat-label">משך זמן</span>
          </div>
        </div>

        <!-- Price Lists Errors List -->
        <Transition name="fade-slide">
          <div v-if="pricesResult.errors && pricesResult.errors.length > 0" class="batch-errors">
            <div class="batch-errors-header">שגיאות (עד 10 ראשונות):</div>
            <ul class="batch-errors-list">
              <li v-for="err in pricesResult.errors" :key="err.priceListId">
                <code>{{ err.priceListId }}</code
                >: {{ err.message }}
              </li>
            </ul>
          </div>
        </Transition>

        <div class="result-footer">
          <span class="result-collection">אוסף: <code>Prices</code></span>
        </div>
      </div>
    </Transition>

    <!-- ============================================ -->
    <!-- Amount Discounts Sync Card -->
    <!-- ============================================ -->
    <div class="action-card">
      <div class="action-card-header">
        <div class="card-icon discounts-icon">
          <svg
            viewBox="0 0 24 24"
            width="20"
            height="20"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M9 14l6-6" />
            <circle cx="9.5" cy="8.5" r="1.5" />
            <circle cx="14.5" cy="13.5" r="1.5" />
            <rect x="2" y="2" width="20" height="20" rx="2" />
          </svg>
        </div>
        <div class="card-header-text">
          <h2 class="action-card-title">הנחות כמות</h2>
          <p class="action-card-desc">
            משיכת הנחות כמות מפריורטי ועדכון Firestore (AmountDiscounts)
          </p>
        </div>
      </div>

      <div class="action-card-body">
        <button
          class="btn-action btn-primary"
          :disabled="discountsLoading"
          @click="handlePullAmountDiscounts"
        >
          <LoadingSpinner v-if="discountsLoading" size="sm" />
          <svg
            v-else
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M9 14l6-6" />
            <circle cx="9.5" cy="8.5" r="1.5" />
            <circle cx="14.5" cy="13.5" r="1.5" />
            <rect x="2" y="2" width="20" height="20" rx="2" />
          </svg>
          <span>{{ discountsLoading ? 'מושך הנחות כמות...' : 'משוך הנחות כמות' }}</span>
        </button>
      </div>
    </div>

    <!-- Amount Discounts Sync Error -->
    <Transition name="fade-slide">
      <div v-if="discountsHasError" class="error-card">
        <div class="error-card-header">
          <span class="error-icon">⚠️</span>
          <span class="error-title">שגיאה במשיכת הנחות כמות</span>
        </div>
        <p class="error-message">{{ discountsErrorMessage }}</p>
      </div>
    </Transition>

    <!-- Amount Discounts Sync Result -->
    <Transition name="fade-slide">
      <div v-if="discountsHasResult" class="result-card">
        <div class="result-card-header">
          <span class="result-icon">✅</span>
          <span class="result-title">משיכת הנחות כמות הושלמה</span>
          <span v-if="discountsLastRunAt" class="result-timestamp">{{
            formatTimestamp(discountsLastRunAt)
          }}</span>
        </div>

        <div class="result-stats result-stats-5">
          <div class="stat-item">
            <span class="stat-value">{{ discountsResult.fetchedCount }}</span>
            <span class="stat-label">שורות נמשכו</span>
          </div>
          <div class="stat-item stat-success">
            <span class="stat-value">{{ discountsResult.assortmentsCount }}</span>
            <span class="stat-label">אסורטימנטים</span>
          </div>
          <div class="stat-item stat-success">
            <span class="stat-value">{{ discountsResult.itemDocsWrittenCount }}</span>
            <span class="stat-label">פריטים עודכנו</span>
          </div>
          <div class="stat-item" :class="{ 'stat-warning': discountsResult.failedCount > 0 }">
            <span class="stat-value">{{ discountsResult.failedCount }}</span>
            <span class="stat-label">נכשלו</span>
          </div>
          <div class="stat-item stat-muted">
            <span class="stat-value">{{ formatDuration(discountsResult.durationMs) }}</span>
            <span class="stat-label">משך זמן</span>
          </div>
        </div>

        <!-- Amount Discounts Errors List -->
        <Transition name="fade-slide">
          <div
            v-if="discountsResult.sampleErrors && discountsResult.sampleErrors.length > 0"
            class="batch-errors"
          >
            <div class="batch-errors-header">שגיאות (עד 10 ראשונות):</div>
            <ul class="batch-errors-list">
              <li v-for="err in discountsResult.sampleErrors" :key="err.assortment">
                <code>{{ err.assortment }}</code
                >: {{ err.message }}
              </li>
            </ul>
          </div>
        </Transition>

        <div class="result-footer">
          <span class="result-collection">אוסף: <code>AmountDiscounts</code></span>
        </div>
      </div>
    </Transition>

    <!-- New Items Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showNewItemsModal" class="modal-overlay" @click.self="showNewItemsModal = false">
          <div class="modal-container">
            <div class="modal-header">
              <h3 class="modal-title">פריטים חדשים</h3>
              <button class="modal-close" @click="showNewItemsModal = false">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="modal-body">
              <div
                v-if="result && result.newItems && result.newItems.length > 0"
                class="new-items-list"
              >
                <div v-for="item in result.newItems" :key="item.makat" class="new-item-row">
                  <code class="new-item-makat">{{ item.makat }}</code>
                  <span class="new-item-name">{{ item.name }}</span>
                </div>
              </div>
              <p v-else class="modal-empty">אין פריטים חדשים</p>
            </div>
            <div class="modal-footer">
              <button class="btn-action btn-secondary" @click="showNewItemsModal = false">
                סגור
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- Removed Items Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div
          v-if="showRemovedItemsModal"
          class="modal-overlay"
          @click.self="showRemovedItemsModal = false"
        >
          <div class="modal-container">
            <div class="modal-header">
              <h3 class="modal-title">פריטים שהוסרו מהקטלוג</h3>
              <button class="modal-close" @click="showRemovedItemsModal = false">
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p class="modal-explanation">פריטים אלו סומנו כ-Z (לא להציג) בפריוריטי והוסרו מהמאגר</p>
            <div class="modal-body">
              <div
                v-if="result && result.removedItems && result.removedItems.length > 0"
                class="new-items-list"
              >
                <div
                  v-for="item in result.removedItems"
                  :key="item.makat"
                  class="new-item-row removed-item-row"
                >
                  <code class="new-item-makat removed-item-makat">{{ item.makat }}</code>
                  <span class="new-item-name">{{ item.name }}</span>
                </div>
              </div>
              <p v-else class="modal-empty">לא הוסרו פריטים</p>
            </div>
            <div class="modal-footer">
              <button class="btn-action btn-secondary" @click="showRemovedItemsModal = false">
                סגור
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.admin-data {
  padding: var(--spacing-lg);
  max-width: 800px;
  margin: 0 auto;
}

/* ============================================
   Page Header - Premium Style
   ============================================ */
.page-header {
  margin-bottom: var(--spacing-xl);
}

.header-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: var(--border-radius-full);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: var(--spacing-sm);
}

.header-icon {
  color: var(--color-primary);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin: 0 0 var(--spacing-xs) 0;
}

.page-subtitle {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin: 0;
}

/* ============================================
   Action Card - Premium Style
   ============================================ */
.action-card {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--border-color);
  overflow: hidden;
  margin-bottom: var(--spacing-lg);
  box-shadow: var(--shadow-sm);
  transition:
    box-shadow var(--transition-fast),
    border-color var(--transition-fast);
}

.action-card:hover {
  box-shadow: var(--shadow-md);
  border-color: rgba(99, 102, 241, 0.2);
}

.action-card-header {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: var(--border-radius);
  flex-shrink: 0;
}

.card-icon.catalog-icon {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%);
  color: var(--color-primary);
}

.card-icon.images-icon {
  background: linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%);
  color: #06b6d4;
}

.card-icon.clients-icon {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%);
  color: var(--color-success);
}

.card-icon.prices-icon {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(217, 119, 6, 0.15) 100%);
  color: #f59e0b;
}

.card-icon.discounts-icon {
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%);
  color: #a855f7;
}

.card-header-text {
  flex: 1;
  min-width: 0;
}

.action-card-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0 0 4px 0;
}

.action-card-desc {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.5;
}

.action-card-body {
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.action-card-body-compact {
  padding: var(--spacing-md) var(--spacing-lg);
  gap: var(--spacing-md);
}

/* Compact Controls Row */
.controls-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  flex-wrap: wrap;
}

.toggle-label {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  padding: 6px 12px;
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
  transition: all var(--transition-fast);
}

.toggle-label:hover {
  border-color: var(--color-primary);
  background: var(--bg-tertiary);
}

.toggle-input {
  width: 16px;
  height: 16px;
  cursor: pointer;
  accent-color: var(--color-primary);
}

.toggle-text {
  font-weight: var(--font-weight-medium);
}

.inline-input {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
}

.inline-input input {
  width: 70px;
  padding: 6px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  background: var(--bg-primary);
  transition: all var(--transition-fast);
}

.inline-input input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.inline-input input:disabled {
  background: var(--bg-secondary);
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-group-inline {
  display: flex;
  gap: var(--spacing-sm);
  margin-right: auto;
}

.btn-sm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-sm:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: var(--shadow-sm);
}

.btn-sm:active:not(:disabled) {
  transform: translateY(0);
}

.btn-sm.btn-primary {
  background: var(--gradient-primary);
  color: var(--color-white);
  box-shadow: 0 1px 4px rgba(99, 102, 241, 0.3);
}

.btn-sm.btn-primary:hover:not(:disabled) {
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.4);
}

.btn-sm.btn-secondary {
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.btn-sm.btn-secondary:hover:not(:disabled) {
  background: var(--bg-secondary);
  border-color: var(--color-primary);
}

.btn-sm.btn-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: var(--color-white);
  box-shadow: 0 1px 4px rgba(245, 158, 11, 0.3);
}

.btn-sm.btn-warning:hover:not(:disabled) {
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.4);
}

/* Checkbox */
.checkbox-label {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-sm);
  cursor: pointer;
}

.checkbox-input {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.checkbox-text {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  font-weight: 500;
}

.checkbox-hint {
  width: 100%;
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  margin-right: 26px;
}

/* Input Group */
.input-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.input-label {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  font-weight: 500;
}

.input-field {
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  max-width: 150px;
}

.input-field:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.input-field:disabled {
  background: var(--bg-secondary);
  cursor: not-allowed;
}

.input-hint {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

/* Button Group */
.btn-group {
  display: flex;
  gap: var(--spacing-md);
  flex-wrap: wrap;
}

/* ============================================
   Action Button - Premium Style
   ============================================ */
.btn-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-xl);
  border: none;
  border-radius: var(--border-radius);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.btn-action:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.btn-action:active:not(:disabled) {
  transform: translateY(0);
}

.btn-action:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-action.btn-primary {
  background: var(--gradient-primary);
  color: var(--color-white);
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
}

.btn-action.btn-primary:hover:not(:disabled) {
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.4);
}

.btn-action.btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
}

.btn-action.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--color-primary);
}

.btn-action.btn-warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: var(--color-white);
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
}

.btn-action.btn-warning:hover:not(:disabled) {
  box-shadow: 0 4px 16px rgba(245, 158, 11, 0.4);
}

/* ============================================
   Error Card - Premium Style
   ============================================ */
.error-card {
  background: linear-gradient(135deg, #fef2f2 0%, #fff5f5 100%);
  border: 1px solid #fecaca;
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.1);
}

.error-card-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-sm);
}

.error-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(239, 68, 68, 0.1);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-sm);
}

.error-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: #dc2626;
}

.error-message {
  font-size: var(--font-size-sm);
  color: #991b1b;
  margin: 0;
  word-break: break-word;
  line-height: 1.5;
}

/* ============================================
   Result Card - Premium Style
   ============================================ */
.result-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  margin-bottom: var(--spacing-lg);
  box-shadow: var(--shadow-sm);
}

.result-card-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border-bottom: 1px solid #bbf7d0;
}

.result-card-header.header-running {
  background: linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%);
  border-bottom-color: #fcd34d;
}

.result-card-header.header-done {
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border-bottom-color: #bbf7d0;
}

.result-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: rgba(16, 185, 129, 0.15);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-sm);
}

.header-running .result-icon {
  background: rgba(245, 158, 11, 0.15);
}

.result-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: #16a34a;
}

.header-running .result-title {
  color: #d97706;
}

.result-timestamp {
  margin-right: auto;
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 8px;
  border-radius: var(--border-radius-full);
}

.result-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-md);
  padding: var(--spacing-lg);
}

.result-stats-4 {
  grid-template-columns: repeat(4, 1fr);
}

.result-stats-5 {
  grid-template-columns: repeat(3, 1fr);
}

.result-stats-6 {
  grid-template-columns: repeat(3, 1fr);
}

@media (min-width: 700px) {
  .result-stats-5 {
    grid-template-columns: repeat(5, 1fr);
  }

  .result-stats-6 {
    grid-template-columns: repeat(6, 1fr);
  }
}

.stat-item {
  text-align: center;
  padding: var(--spacing-md);
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color-light);
  transition:
    transform var(--transition-fast),
    box-shadow var(--transition-fast);
}

.stat-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-sm);
}

.stat-value {
  display: block;
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-xs);
}

.stat-label {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
}

.stat-success .stat-value {
  color: #16a34a;
}

.stat-success {
  border-color: rgba(16, 185, 129, 0.2);
  background: linear-gradient(180deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.1) 100%);
}

.stat-warning .stat-value {
  color: #ca8a04;
}

.stat-warning {
  border-color: rgba(202, 138, 4, 0.2);
  background: linear-gradient(180deg, rgba(202, 138, 4, 0.05) 0%, rgba(202, 138, 4, 0.1) 100%);
}

.stat-info .stat-value {
  color: #2563eb;
}

.stat-info {
  border-color: rgba(37, 99, 235, 0.2);
  background: linear-gradient(180deg, rgba(37, 99, 235, 0.05) 0%, rgba(37, 99, 235, 0.1) 100%);
}

.stat-danger .stat-value {
  color: #dc2626;
}

.stat-danger {
  border-color: rgba(220, 38, 38, 0.2);
  background: linear-gradient(180deg, rgba(220, 38, 38, 0.05) 0%, rgba(220, 38, 38, 0.1) 100%);
}

.stat-clickable.stat-has-items {
  cursor: pointer;
  position: relative;
}

.stat-clickable.stat-has-items:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-md);
}

.stat-clickable.stat-has-items.stat-info:hover {
  border-color: rgba(37, 99, 235, 0.4);
}

.stat-clickable.stat-has-items.stat-danger:hover {
  border-color: rgba(220, 38, 38, 0.4);
}

.stat-clickable.stat-has-items .stat-label::after {
  content: ' \203A';
}

.stat-muted .stat-value {
  color: var(--text-secondary);
}

.result-footer {
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid var(--border-color);
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%);
}

.result-collection {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
}

.result-collection code {
  background: var(--bg-primary);
  padding: 3px 8px;
  border-radius: var(--border-radius-sm);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-xs);
  color: var(--color-primary);
  border: 1px solid var(--border-color);
}

/* Batch Errors */
.batch-errors {
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid var(--border-color);
  background: linear-gradient(135deg, #fef2f2 0%, #fff5f5 100%);
}

.batch-errors-header {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: #dc2626;
  margin-bottom: var(--spacing-sm);
}

.batch-errors-list {
  margin: 0;
  padding-right: var(--spacing-lg);
  font-size: var(--font-size-xs);
  color: #991b1b;
  line-height: 1.6;
}

.batch-errors-list li {
  margin-bottom: var(--spacing-xs);
}

.batch-errors-list code {
  background: rgba(220, 38, 38, 0.1);
  padding: 2px 6px;
  border-radius: var(--border-radius-sm);
  font-family: var(--font-family-mono);
  font-size: 11px;
}

/* Transitions */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* ============================================
   Modal
   ============================================ */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-lg);
}

.modal-container {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 520px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-lg);
  border-bottom: 1px solid var(--border-color);
  background: linear-gradient(180deg, var(--bg-secondary) 0%, var(--bg-primary) 100%);
}

.modal-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: var(--border-radius);
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.modal-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: var(--spacing-lg);
  overflow-y: auto;
  flex: 1;
}

.modal-empty {
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
  padding: var(--spacing-xl) 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-start;
  padding: var(--spacing-md) var(--spacing-lg);
  border-top: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.new-items-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.new-item-row {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color-light);
}

.new-item-makat {
  flex-shrink: 0;
  background: rgba(37, 99, 235, 0.1);
  color: #2563eb;
  padding: 2px 8px;
  border-radius: var(--border-radius-sm);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
}

.new-item-name {
  font-size: var(--font-size-sm);
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-explanation {
  padding: var(--spacing-sm) var(--spacing-lg);
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.removed-item-row {
  border-color: rgba(220, 38, 38, 0.15);
}

.removed-item-makat {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container {
  transform: scale(0.95) translateY(10px);
}

.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(10px);
}

/* Responsive */
@media (max-width: 600px) {
  .admin-data {
    padding: var(--spacing-md);
  }

  .result-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .result-stats-4 {
    grid-template-columns: repeat(2, 1fr);
  }

  .btn-group {
    flex-direction: column;
  }

  .btn-action {
    width: 100%;
  }

  .modal-overlay {
    padding: 0;
  }

  .modal-container {
    max-width: 100%;
    max-height: 100vh;
    height: 100%;
    border-radius: 0;
  }
}
</style>
